import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import axios from 'axios';
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { isServerError, isTransientError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

interface LoginFormValues {
  login: string;
  password: string;
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authed') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  const onFinish = async (values: LoginFormValues): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await login(values.login.trim(), values.password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/login' ? from : '/', { replace: true });
    } catch (err) {
      const tooMany = axios.isAxiosError(err) && err.response?.status === 429;
      // Timeout / offline / api restarting: say so — «wrong password» would
      // send the owner re-typing a password that was never even checked.
      setError(
        tooMany
          ? t('login.tooMany')
          : isServerError(err)
            ? t('login.serverError')
            : isTransientError(err)
              ? t('login.unreachable')
              : t('login.failed'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 380 }}>
        <Typography.Title level={3} style={{ textAlign: 'center', marginBottom: 4 }}>
          {t('app.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ textAlign: 'center' }}>
          {t('login.subtitle')}
        </Typography.Paragraph>
        {error ? (
          <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />
        ) : null}
        <Form<LoginFormValues>
          layout="vertical"
          requiredMark={false}
          onFinish={(v) => void onFinish(v)}
        >
          <Form.Item
            name="login"
            label={t('login.loginLabel')}
            rules={[{ required: true, message: t('login.loginRequired') }]}
          >
            <Input
              size="large"
              prefix={<UserOutlined />}
              autoComplete="username"
              autoCapitalize="none"
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={t('login.passwordLabel')}
            rules={[{ required: true, message: t('login.passwordRequired') }]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              autoComplete="current-password"
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
            {t('login.submit')}
          </Button>
        </Form>
      </Card>
    </div>
  );
}
